import {
  Box,
  Button,
  FormLabel,
  TextField,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Field, Form, Formik } from 'formik';
import React from 'react';
import * as Yup from 'yup';

// Validation schema with Yup
const formSchema = Yup.object({
  trigger: Yup.string().required('Trigger Name is required'),
  description: Yup.string().required('Description is required'),
});

interface MacroAddFormProps {
  onOpenChange: (open: boolean) => void;
  setToast: (toast: any) => void;
  initialValues?: {
    trigger: string;
    description: string;
    id?: number;
  };
  isEditMode?: boolean;
  setTextMacros?: React.Dispatch<
    React.SetStateAction<{ id: number; trigger: string; text: string }[]>
  >;
}

export const MacroAddForm = ({
  onOpenChange,
  setToast,
  initialValues = { trigger: '', description: '' },
  isEditMode = false,
  setTextMacros,
}: MacroAddFormProps) => {
  const userRolesData = localStorage.getItem('roles') || '';
  const userData = userRolesData ? JSON.parse(userRolesData) : null;

  const theme = useTheme();
  const underSm = useMediaQuery(theme.breakpoints.down('sm'));
  const isMobile375 = useMediaQuery('(max-width:375px)');

  const baseUrl = process.env.REACT_APP_API_BASE_URL;

  // Handle API call for add and edit
  const handleApiCall = async (values: any) => {
    const token = localStorage.getItem('AUTH_TOKEN');
    if (!values.trigger || !values.description) {
      setToast({
        open: true,
        message: 'Please enter both Trigger Name and Description',
        severity: 'error',
      });
      return;
    }

    if (isEditMode) {
      // For update operation
      try {
        const updatedMacro = {
          trigger: values.trigger,
          text: values.description,
          userid: userData?.id,
        };

        const response = await fetch(`${baseUrl}/api/textmacros/${values.id}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(updatedMacro),
        });

        if (!response.ok) throw new Error('Failed to update macro');
        const updatedMacroData = await response.json();

        if (setTextMacros) {
          setTextMacros((prev) =>
            prev.map((macro) =>
              macro.id === values.id ? updatedMacroData : macro
            )
          );
        }

        setToast({
          open: true,
          message: 'Text macro updated successfully!',
          severity: 'success',
        });
        onOpenChange(false);
      } catch (error) {
        console.error('Error updating macro:', error);
        setToast({
          open: true,
          message: 'Failed to update text macro. Please try again.',
          severity: 'error',
        });
      }
    } else {
      try {
        // For create operation
        const newMacro = {
          trigger: values.trigger,
          text: values.description,
          userid: userData?.id,
        };

        const response = await fetch(`${baseUrl}/api/textmacros`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(newMacro),
        });

        if (!response.ok) throw new Error('Failed to add macro');
        const addedMacro = await response.json();

        if (setTextMacros) {
          setTextMacros((prev) => [...prev, addedMacro]);
        }

        setToast({
          open: true,
          message: 'Text macro created successfully!',
          severity: 'success',
        });
        onOpenChange(false);
      } catch (error) {
        console.error('Error adding macro:', error);
        setToast({
          open: true,
          message: 'Failed to create text macro. Please try again.',
          severity: 'error',
        });
      }
    }
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={formSchema}
      enableReinitialize
      onSubmit={handleApiCall}
    >
      {({ errors, touched }: any) => (
        <Form
          className={`mx-auto mt-2 ${
            isMobile375 ? 'w-[362px]' : 'w-[400px]'
          } space-y-6 rounded-md p-3 px-0 pl-1`}
        >
          <div
            className={`flex ${
              underSm ? 'h-[calc(94vh-170px)]' : 'h-[calc(100vh-170px)]'
            } flex-col gap-3 overflow-auto pr-2`}
          >
            <Box>
              <FormLabel htmlFor="trigger" sx={{ fontSize: '0.9rem' }}>
                Trigger
              </FormLabel>
              <Field
                id="trigger"
                className="mt-1"
                name="trigger"
                as={TextField}
                fullWidth
                variant="outlined"
                error={touched.trigger && !!errors.trigger}
                helperText={touched.trigger && errors.trigger}
                InputProps={{ sx: { fontSize: '0.9rem' } }}
              />
            </Box>

            <Box>
              <FormLabel htmlFor="description" sx={{ fontSize: '0.9rem' }}>
                Description
              </FormLabel>
              <Field
                id="description"
                className="mt-1"
                name="description"
                as={TextField}
                fullWidth
                multiline
                rows={8}
                variant="outlined"
                error={touched.description && !!errors.description}
                helperText={touched.description && errors.description}
                InputProps={{ sx: { fontSize: '0.9rem' } }}
              />
            </Box>
          </div>

          <div className="flex items-center justify-between pr-2">
            <Button
              variant="outlined"
              type="button"
              onClick={() => onOpenChange(false)}
              color="secondary"
              className="w-[48%] py-2"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              className="w-[48%] bg-[#11AC9E] py-2 hover:bg-[#90BEE6]"
            >
              {isEditMode ? 'Save Changes' : 'Add Macro'}
            </Button>
          </div>
        </Form>
      )}
    </Formik>
  );
};
